import { Checkbox } from "@/components/ui/checkbox";
import { flagClass, actionClass, priceLabel, vendorLabel, fmtAmt, fmtDate, freshness } from "@/lib/flags";
import { ArrowDownNarrowWide, IndianRupee } from "lucide-react";
import { auth } from "@/lib/api";

const fmtSaving = (v) => {
  if (v == null || v === 0) return "0";
  const abs = Math.abs(v);
  if (abs >= 10000000) return (v / 10000000).toFixed(2).replace(/\.?0+$/, "") + " Cr";
  if (abs >= 100000) return (v / 100000).toFixed(2).replace(/\.?0+$/, "") + " L";
  if (abs >= 1000) return (v / 1000).toFixed(1).replace(/\.?0+$/, "") + " K";
  return Math.round(v).toString();
};

// Compact suffix describing WHY a PR is on HOLD:
//   manual hold placed by current user -> "By me"
//   manual hold placed by someone else -> "By <Name>"
//   AI-recommended HOLD (high risk score) -> "AI"
function holdSubLabel(r, currentUser) {
  if (r.ai_action !== "HOLD") return null;
  const info = r.hold_info;
  if (!info) return null;
  if (info.source === "manual") {
    const myEmp = (currentUser?.emp_no || "").trim();
    if (myEmp && info.by_emp_no && info.by_emp_no === myEmp) return "By me";
    const nm = (info.by_name || "").trim();
    if (nm) {
      // Trim long names to first + last initial
      const ps = nm.split(/\s+/);
      const short = ps.length > 1 ? `${ps[0]} ${ps[ps.length - 1][0]}.` : nm;
      return `By ${short}`;
    }
    return "Manual";
  }
  return "AI";
}

function SwitchPill({ pr, mode = "row" }) {
  const alt = pr.best_alt_vendor;
  const saving = pr.potential_saving || 0;
  if (!alt || saving < 100) return null;
  const compact = mode === "mobile";
  const fr = freshness(alt.vdate);
  const dateNote = alt.vdate ? ` (last seen ${fmtDate(alt.vdate)}${fr ? `, ${fr.ago}` : ""})` : "";
  return (
    <span
      title={`Switch to ${alt.vendor} → save ₹${Math.round(saving).toLocaleString()} across ${alt.items} item${alt.items === 1 ? "" : "s"}${dateNote}`}
      data-testid={`switch-pill-${pr.voc_no}`}
      className={`inline-flex items-center gap-1 ${compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"} rounded-full font-semibold bg-amber-50 text-amber-900 border border-amber-200`}
    >
      <IndianRupee size={compact ? 9 : 10} className="text-amber-700" />
      Save ₹{fmtSaving(saving)}
      {(alt.vendor || alt.vdate) && (
        <span className="text-amber-700 font-normal hidden sm:inline truncate max-w-[200px] flex items-center gap-1">
          · {alt.vendor}
          {alt.vdate && fr && (
            <>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${fr.dotClass}`} aria-label={fr.tier} />
              <span className={fr.textClass}>{fmtDate(alt.vdate)}</span>
            </>
          )}
        </span>
      )}
    </span>
  );
}

export default function PRTable({ rows, selected, setSelected, onOpen, busy }) {
  const currentUser = auth.user();
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.voc_no));
  const toggleAll = () => {
    if (allSelected) setSelected([]);
    else setSelected(rows.map((r) => r.voc_no));
  };
  const toggle = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  return (
    <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
      {/* Mobile bulk-select bar */}
      <div className="md:hidden flex items-center justify-between px-3 py-2 bg-cyan-700 text-white">
        <label className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold cursor-pointer">
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleAll}
            data-testid="select-all-checkbox-mobile"
            className="border-white data-[state=checked]:bg-white data-[state=checked]:text-cyan-800"
          />
          Select all
        </label>
        <span className="text-xs text-cyan-100">{rows.length} PR{rows.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden divide-y divide-slate-100 scroll-area overflow-y-auto" style={{ maxHeight: "calc(100vh - 360px)" }}>
        {rows.length === 0 && (
          <div className="text-center py-12 text-slate-500 text-sm" data-testid="empty-state-mobile">
            {busy ? "Loading PRs from MS SQL..." : "No PRs match the filters."}
          </div>
        )}
        {rows.map((r) => {
          const isSel = selected.includes(r.voc_no);
          return (
            <div
              key={r.voc_no}
              data-testid={`pr-card-${r.voc_no}`}
              className="p-3 flex flex-col gap-2"
            >
              <div className="flex items-start gap-2">
                <Checkbox
                  checked={isSel}
                  onCheckedChange={() => toggle(r.voc_no)}
                  data-testid={`select-mobile-${r.voc_no}`}
                  className="mt-1"
                />
                <button
                  type="button"
                  onClick={() => onOpen(r.voc_no)}
                  data-testid={`open-mobile-${r.voc_no}`}
                  className="flex-1 text-left"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono font-semibold text-slate-900 text-sm">{r.voc_no}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-800 uppercase tracking-wider border border-cyan-200 mono">
                        {r.plant || "—"}
                      </span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 uppercase tracking-wider">
                        {r.purc_type}
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${actionClass(r.ai_action)}`}
                      data-testid={`ai-action-mobile-${r.voc_no}`}
                    >
                      {r.ai_action === "HOLD" ? <ArrowDownNarrowWide size={10} /> : null}
                      {r.ai_action}
                      {holdSubLabel(r, currentUser) && (
                        <span className="font-semibold opacity-80">· {holdSubLabel(r, currentUser)}</span>
                      )}
                    </span>
                  </div>
                  <div className="text-sm text-slate-800 mt-1 line-clamp-2" title={r.supplier}>
                    {r.supplier}
                  </div>
                  <div className="flex items-center justify-between mt-1 text-xs">
                    <span className="text-slate-500 num">{fmtDate(r.vdate)}</span>
                    <span className="font-semibold num text-slate-900">₹ {fmtAmt(r.amount)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${flagClass(r.price_flag.flag)}`}>
                      Price · {priceLabel(r.price_flag)}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${flagClass(r.vendor_flag.flag)}`}>
                      Vendor · {vendorLabel(r.vendor_flag)}
                    </span>
                    <SwitchPill pr={r} mode="mobile" />
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-slate-50 text-slate-700 border-slate-200">
                      Score {r.risk_score}
                    </span>
                    {r.req_status && r.req_status !== "Open" && (
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-cyan-50 text-cyan-800 border-cyan-200">
                        {r.req_status}
                      </span>
                    )}
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table (md+) */}
      <div className="hidden md:block overflow-x-auto scroll-area" style={{ maxHeight: "calc(100vh - 380px)" }}>
        <table className="w-full text-sm">
          <thead className="bg-cyan-700 text-white sticky top-0 z-10">
            <tr className="text-left">
              <th className="px-3 py-3 w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  data-testid="select-all-checkbox"
                  className="border-white data-[state=checked]:bg-white data-[state=checked]:text-cyan-800"
                />
              </th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">PR No</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Plant</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Type</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Vdate</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Supplier</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-right">Amount</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Price Flag</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Vendor Flag</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">AI Recommendation</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-right">Score</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Approval</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} className="text-center py-16 text-slate-500" data-testid="empty-state">
                  {busy ? "Loading PRs from MS SQL..." : "No PRs match the filters."}
                </td>
              </tr>
            )}
            {rows.map((r, i) => {
              const isSel = selected.includes(r.voc_no);
              return (
                <tr
                  key={r.voc_no}
                  data-testid={`pr-row-${r.voc_no}`}
                  onClick={() => onOpen(r.voc_no)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(r.voc_no); } }}
                  aria-label={`Open PR ${r.voc_no}`}
                  className={`pr-row border-t border-slate-100 cursor-pointer ${i % 2 === 1 ? "pr-row-odd" : ""}`}
                >
                  <td
                    className="px-3 py-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={() => toggle(r.voc_no)}
                      data-testid={`select-${r.voc_no}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono font-semibold text-cyan-800 underline-offset-2 hover:underline" data-testid={`open-${r.voc_no}`}>{r.voc_no}</td>
                  <td className="px-3 py-2"><span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-300 mono" data-testid={`plant-${r.voc_no}`}>{r.plant || "—"}</span></td>
                  <td className="px-3 py-2 text-slate-700">{r.purc_type}</td>
                  <td className="px-3 py-2 text-slate-700 num">{fmtDate(r.vdate)}</td>
                  <td className="px-3 py-2 text-slate-800 max-w-[260px] truncate" title={r.supplier}>
                    {r.supplier}
                  </td>
                  <td className="px-3 py-2 text-right num font-medium text-slate-900">
                    {fmtAmt(r.amount)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${flagClass(r.price_flag.flag)}`}>
                      {priceLabel(r.price_flag)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1 items-start">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${flagClass(r.vendor_flag.flag)}`}>
                        {vendorLabel(r.vendor_flag)}
                      </span>
                      <SwitchPill pr={r} mode="row" />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${actionClass(r.ai_action)}`}
                      data-testid={`ai-action-${r.voc_no}`}
                      title={r.hold_info?.source === "manual" && r.hold_info?.note ? `Held: ${r.hold_info.note}` : undefined}
                    >
                      {r.ai_action === "HOLD" ? <ArrowDownNarrowWide size={12} /> : null}
                      {r.ai_action}
                      {holdSubLabel(r, currentUser) && (
                        <span className="font-semibold opacity-80">· {holdSubLabel(r, currentUser)}</span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right num font-bold text-slate-900">{r.risk_score}</td>
                  <td className="px-3 py-2 text-slate-700">{r.req_status || "Open"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
